import { Link } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { Package, Plus, Search, Archive, ArchiveRestore, Camera, Pencil, Tags, Check, X, Power, Image as ImageIcon, Trash2, MapPin, Upload, Percent } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { inv, INV_API } from '../../lib/invApi';
import ScanButton from '../../components/inventory/ScanButton';
import BulkPricingDialog from '../../components/inventory/BulkPricingDialog';
import MasterInventoryTable from '../../components/inventory/MasterInventoryTable';
import { TradeFilterChips, InventoryAttributeFilters } from '../../components/inventory/InventoryFilters';
import BulkActionsBar from '../../components/inventory/BulkActionsBar';
import InventorySummaryStrip from '../../components/inventory/InventorySummaryStrip';
import InventoryExportButtons from '../../components/inventory/InventoryExportButtons';

const blank = () => ({ name: '', sku: '', barcode: '', size: '', department: '', departments: [], category: '', category_id: '', brand: '', preferred_supplier: '', unit: 'ea', default_cost: 0, markup_pct: '', sell_price: '', sell_price_override: false, min_stock_company: 0, description: '', notes: '', vendor_sku: '' });

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
// Markup ON COST (not margin): Sell = Cost × (1 + Markup% / 100).
const suggestedSell = (cost, markup) => {
  const c = parseFloat(cost);
  if (isNaN(c)) return '';
  const m = (markup === '' || markup === null || markup === undefined) ? 0 : parseFloat(markup);
  return round2(c * (1 + (isNaN(m) ? 0 : m) / 100));
};
const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

const PAGE_SIZE = 100;
const FILTER_KEY = 'nexflow_inv_master_filters_v1';
const initialFilters = () => {
  try { return { trade: '', category: '', supplier: '', brand: '', status: 'active', low: false, sort: 'name', dir: 'asc', ...JSON.parse(sessionStorage.getItem(FILTER_KEY) || '{}') }; }
  catch { return { trade: '', category: '', supplier: '', brand: '', status: 'active', low: false, sort: 'name', dir: 'asc' }; }
};

const InventoryItems = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [tradeCounts, setTradeCounts] = useState({});
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [brandsAll, setBrandsAll] = useState([]);
  const [suppliersAll, setSuppliersAll] = useState([]);
  const [departmentsAll, setDepartmentsAll] = useState([]);
  const [categoriesAll, setCategoriesAll] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sizes, setSizes] = useState([]);
  const [viewId, setViewId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [photoVer, setPhotoVer] = useState(0);
  const [pendingPhoto, setPendingPhoto] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQDebounced(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  // Selection never carries over hidden rows: reset whenever the visible set changes.
  useEffect(() => { setPage(1); setSelected(new Set()); }, [qDebounced, filters]);
  useEffect(() => { try { sessionStorage.setItem(FILTER_KEY, JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);
  const setF = useCallback((patch) => setFilters((f) => ({ ...f, ...patch })), []);

  // One paged request returns rows + Controlled On Hand + Trade chip counts.
  const reqSeq = React.useRef(0);
  const refresh = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await inv.get('/items', {
        page, page_size: PAGE_SIZE, q: qDebounced || undefined, trade: filters.trade || undefined,
        category: filters.category || undefined, supplier: filters.supplier || undefined, brand: filters.brand || undefined,
        status: filters.status, low_stock: filters.low || undefined, sort: filters.sort, dir: filters.dir,
      });
      if (seq !== reqSeq.current) return;
      setItems(data.items); setTotal(data.total); setTradeCounts(data.trade_counts || {}); setSummary(data.summary || null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load inventory'); }
    if (seq === reqSeq.current) setLoading(false);
  }, [page, qDebounced, filters]);
  const onSort = useCallback((key) => setFilters((f) => ({ ...f, sort: key, dir: f.sort === key && f.dir === 'asc' ? 'desc' : 'asc' })), []);

  const loadLookups = useCallback(async () => {
    const [b, s, d, c, sz] = await Promise.all([
      inv.get('/brands', { include_inactive: true }),
      inv.get('/suppliers', { include_inactive: true }),
      inv.get('/departments', { include_inactive: true, type: 'trade' }),
      inv.get('/categories', { include_inactive: true }),
      inv.get('/item-sizes'),
    ]);
    setBrandsAll(b.data); setSuppliersAll(s.data); setDepartmentsAll(d.data); setCategoriesAll(c.data); setSizes(sz.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // Category options are scoped to the selected Department: active categories in that
  // department, plus the item's currently-assigned category (even if now inactive).
  const categoryOptions = categoriesAll.filter((c) => (form.departments || []).includes(c.department) && (c.active !== false || c.id === form.category_id));
  // Active options for the dropdowns, plus the currently-assigned value even if it was deactivated.
  const optsFor = (all, current) => {
    const names = all.filter((x) => x.active !== false).map((x) => x.name);
    if (current && !names.includes(current)) names.push(current + ' (inactive)');
    return names;
  };

  const openCreate = () => { setEditing(null); setForm(blank()); setHasPhoto(false); setPendingPhoto(null); setPhotoVer(0); setOpen(true); };
  const openView = (id) => { setViewId(id); setViewOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setHasPhoto(!!it.photo_file_id); setPendingPhoto(null); setPhotoVer(0);
    // Backend stores null for unset optional fields; coerce null/undefined back to
    // the blank defaults so controlled inputs never receive null.
    const b = blank();
    const merged = { ...b, ...it };
    Object.keys(b).forEach((k) => { if (merged[k] === null || merged[k] === undefined) merged[k] = b[k]; });
    merged.departments = it.departments || (it.department ? [it.department] : []);
    merged.markup_pct = it.markup_pct ?? '';
    merged.sell_price = it.sell_price ?? '';
    merged.sell_price_override = it.sell_price_override ?? (it.sell_price != null);
    setForm(merged);
    setOpen(true);
  };

  // Cost / Markup changes recompute the suggested Sell Price UNLESS the admin has
  // manually overridden it. A manual Sell edit sets the override flag.
  const onCost = (v) => setForm((f) => ({ ...f, default_cost: v, sell_price: f.sell_price_override ? f.sell_price : suggestedSell(v, f.markup_pct) }));
  const onMarkup = (v) => setForm((f) => ({ ...f, markup_pct: v, sell_price: f.sell_price_override ? f.sell_price : suggestedSell(f.default_cost, v) }));
  const onSell = (v) => setForm((f) => ({ ...f, sell_price: v, sell_price_override: true }));
  const useCalculated = () => setForm((f) => ({ ...f, sell_price: suggestedSell(f.default_cost, f.markup_pct), sell_price_override: false }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        default_cost: parseFloat(form.default_cost) || 0,
        min_stock_company: parseFloat(form.min_stock_company) || 0,
        brand: form.brand.replace(/ \(inactive\)$/, ''),
        preferred_supplier: form.preferred_supplier.replace(/ \(inactive\)$/, ''),
        departments: form.departments || [],
      };
      delete payload.department;
      payload.markup_pct = (form.markup_pct === '' || form.markup_pct === null) ? null : parseFloat(form.markup_pct);
      if (form.sell_price === '' || form.sell_price === null) delete payload.sell_price; else payload.sell_price = parseFloat(form.sell_price);
      if (editing) await inv.put(`/items/${editing.id}`, payload);
      else {
        const created = await inv.post('/items', payload);
        if (pendingPhoto && created?.data?.id) {
          try {
            const fd = new FormData(); fd.append('file', pendingPhoto);
            const { default: axios } = await import('axios');
            await axios.post(`${INV_API}/items/${created.data.id}/photo?kind=item`, fd, { withCredentials: true });
          } catch { /* photo optional — item still created */ }
        }
      }
      toast.success(editing ? 'Item updated' : 'Item created');
      setOpen(false); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  // Photo management inside the New/Edit modal (canonical inv_items.photo_file_id / GridFS).
  const modalUploadPhoto = async (file) => {
    if (!file) return;
    if (!editing) { setPendingPhoto(file); return; }  // defer until create
    const fd = new FormData(); fd.append('file', file);
    try {
      const { default: axios } = await import('axios');
      await axios.post(`${INV_API}/items/${editing.id}/photo?kind=item`, fd, { withCredentials: true });
      setHasPhoto(true); setPhotoVer((v) => v + 1); toast.success('Photo saved'); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };
  const modalRemovePhoto = async () => {
    if (!editing) { setPendingPhoto(null); return; }
    if (!window.confirm('Remove this product photo?')) return;
    try {
      const { default: axios } = await import('axios');
      await axios.delete(`${INV_API}/items/${editing.id}/photo?kind=item`, { withCredentials: true });
      setHasPhoto(false); setPhotoVer((v) => v + 1); toast.success('Photo removed'); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };
  const archiveFromModal = async () => {
    if (!editing) return;
    const restore = editing.archived;
    if (!window.confirm(restore ? `Reactivate ${editing.name}?` : `Archive ${editing.name}? It will be hidden from active inventory but all history is kept.`)) return;
    try {
      await inv.patch(`/items/${editing.id}/${restore ? 'unarchive' : 'archive'}`);
      toast.success(restore ? 'Item reactivated' : 'Item archived');
      setOpen(false); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const deleteFromModal = async () => {
    if (!editing) return;
    if (!window.confirm(`Permanently DELETE ${editing.name}? This only works if the item has never been stocked, moved, scanned or used in a loadout.`)) return;
    try {
      await inv.delete(`/items/${editing.id}`);
      toast.success('Item permanently deleted');
      setOpen(false); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cannot delete — archive instead'); }
  };

  const archive = useCallback(async (it) => {
    if (!window.confirm(`Archive ${it.name}?`)) return;
    try { await inv.patch(`/items/${it.id}/archive`); toast.success('Archived'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }, [refresh]);
  const unarchive = useCallback(async (it) => { try { await inv.patch(`/items/${it.id}/unarchive`); toast.success('Restored'); refresh(); } catch { toast.error('Failed'); } }, [refresh]);

  const uploadPhoto = useCallback(async (it, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { default: axios } = await import('axios');
      await axios.post(`${INV_API}/items/${it.id}/photo?kind=item`, fd, { withCredentials: true });
      toast.success('Photo uploaded'); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  }, [refresh]);

  const suggested = suggestedSell(form.default_cost, form.markup_pct);
  const toggleSel = useCallback((id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const openViewCb = useCallback((id) => openView(id), []);  // eslint-disable-line react-hooks/exhaustive-deps
  const openEditRef = React.useRef(openEdit); openEditRef.current = openEdit;
  const openEditCb = useCallback((it) => openEditRef.current(it), []);
  const activeTrades = departmentsAll.filter((d) => d.active !== false);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allVisibleSelected = items.length > 0 && items.every((it) => selected.has(it.id));
  const toggleSelAll = () => setSelected((s) => { const n = new Set(s); if (allVisibleSelected) items.forEach((it) => n.delete(it.id)); else items.forEach((it) => n.add(it.id)); return n; });

  return (
    <div data-testid="inv-items-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><Package className="w-7 h-7 text-[#D4AF37]" /> Master Inventory</h2>
          <p className="text-[#A3A3A3]">Every item exists once. Locations and quantities are tracked separately.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="inv-manage-lists-btn" onClick={() => setManageOpen(true)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Tags className="w-4 h-4 mr-2" /> Manage Lists</Button>
          <Button data-testid="inv-import-btn" onClick={() => setImportOpen(true)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Upload className="w-4 h-4 mr-2" /> Import Inventory</Button>
          <Button data-testid="inv-bulk-price-btn" onClick={() => setBulkOpen(true)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Percent className="w-4 h-4 mr-2" /> Bulk Pricing{selected.size > 0 ? ` (${selected.size})` : ''}</Button>
          <InventoryExportButtons params={{ q: qDebounced || undefined, trade: filters.trade || undefined, category: filters.category || undefined,
            supplier: filters.supplier || undefined, brand: filters.brand || undefined, status: filters.status, low_stock: filters.low || undefined, sort: filters.sort, dir: filters.dir }} />
          <ScanButton mode="scan" testId="master-scan-btn" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]" onDone={refresh} />
          <Button data-testid="inv-item-new-btn" onClick={openCreate} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Item</Button>
        </div>
      </div>

      <TradeFilterChips trades={activeTrades} value={filters.trade} onChange={(t) => setF({ trade: t })} counts={tradeCounts} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="inv-item-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, barcode, category, brand, supplier…" className="pl-10 bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
        <InventoryAttributeFilters f={filters} set={setF} categories={categoriesAll} suppliers={suppliersAll} brands={brandsAll} />
      </div>

      <InventorySummaryStrip s={summary} scopeLabel={filters.trade && filters.trade !== '__none__' ? `${filters.trade} ` : filters.trade === '__none__' ? 'No Trade ' : ''} />

      <BulkActionsBar selectedIds={[...selected]} trades={activeTrades} categories={categoriesAll} suppliers={suppliersAll}
        onClear={() => setSelected(new Set())} onDone={() => { setSelected(new Set()); refresh(); }} onBulkPricing={() => setBulkOpen(true)} />

      <MasterInventoryTable items={items} sort={filters.sort} dir={filters.dir} onSort={onSort} selected={selected}
        onToggle={toggleSel} allSelected={allVisibleSelected} onToggleAll={toggleSelAll} loading={loading}
        emptyText={`No items${q || filters.trade || filters.category || filters.supplier ? ' for these filters' : ''}.`}
        onOpen={openViewCb} onEdit={openEditCb} onArchive={archive} onUnarchive={unarchive} onPhoto={uploadPhoto} />

      <div className="flex items-center justify-between text-xs text-[#A3A3A3]" data-testid="inv-items-pager">
        <span data-testid="inv-items-total">{total} item{total === 1 ? '' : 's'}{selected.size ? ` · ${selected.size} selected` : ''} · On Hand = every Shop + Warehouse location (exact). Vans are estimated and not included.</span>
        {pages > 1 && (
          <div className="flex items-center gap-2">
            <Button data-testid="inv-items-prev" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-8 border-[#262626]">Prev</Button>
            <span data-testid="inv-items-page">Page {page} / {pages}</span>
            <Button data-testid="inv-items-next" size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="h-8 border-[#262626]">Next</Button>
          </div>
        )}
      </div>

      {/* ── Item create/edit ───────────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inv-item-dialog" className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name}` : 'New Inventory Item'}</DialogTitle><DialogDescription className="text-[#A3A3A3] text-xs">Manage this item's details, product photo, pricing and Size. Use Archive to retire an item while keeping its history.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name *</Label><Input data-testid="inv-item-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>

            <div className="col-span-2">
              <Label className="text-xs">Product Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div data-testid="inv-item-form-photo" className="w-20 h-20 shrink-0 rounded-md border border-[#262626] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                  {(editing && hasPhoto) ? (
                    <img data-testid="inv-item-form-photo-img" src={`${INV_API}/items/${editing.id}/photo?kind=item&v=${photoVer}`} alt={form.name} className="w-full h-full object-contain" />
                  ) : pendingPhoto ? (
                    <img data-testid="inv-item-form-photo-img" src={URL.createObjectURL(pendingPhoto)} alt="preview" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-7 h-7 text-[#525252]" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="cursor-pointer">
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="inv-item-form-photo-upload" onChange={(e) => modalUploadPhoto(e.target.files?.[0])} />
                    <Button asChild size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 pointer-events-none"><span><Camera className="w-4 h-4 mr-1" />{(hasPhoto || pendingPhoto) ? 'Replace Photo' : 'Upload Photo'}</span></Button>
                  </label>
                  {(hasPhoto || pendingPhoto) && <Button data-testid="inv-item-form-photo-remove" onClick={modalRemovePhoto} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-8"><Trash2 className="w-4 h-4 mr-1" /> Remove Photo</Button>}
                  {!editing && <p className="text-[10px] text-[#525252]">Photo uploads when you create the item.</p>}
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <Label className="text-xs">Size <span className="text-[#525252]">(e.g. 1/2" x 3/8", 3/4", 10 ft — kept separate from the name)</span></Label>
              <Input data-testid="inv-item-form-size" list="inv-size-suggestions" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder='1/2" x 3/8"' className="bg-[#0A0A0A] border-[#262626] text-white" />
              <datalist id="inv-size-suggestions">{sizes.map((s) => <option key={s} value={s} />)}</datalist>
            </div>

            <div>
              <Label className="text-xs">Trades <span className="text-[#525252]">(one or more)</span></Label>
              <div data-testid="inv-item-form-trades" className="flex flex-wrap gap-1.5 mt-1">
                {departmentsAll.filter((d) => d.active !== false || (form.departments || []).includes(d.name)).map((d) => {
                  const on = (form.departments || []).includes(d.name);
                  return (
                    <button key={d.id} type="button" data-testid={`inv-item-form-trade-${d.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      onClick={() => setForm((f) => {
                        const next = on ? f.departments.filter((x) => x !== d.name) : [...(f.departments || []), d.name];
                        const cat = categoriesAll.find((c) => c.id === f.category_id);
                        return { ...f, departments: next, ...(cat && !next.includes(cat.department) ? { category_id: '', category: '' } : {}) };
                      })}
                      className={`px-2.5 h-7 rounded-full text-xs border transition-colors ${on ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>{d.name}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <select data-testid="inv-item-form-category" value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })} disabled={!(form.departments || []).length} className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3 disabled:opacity-50">
                <option value="">Unassigned</option>
                {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}{(form.departments || []).length > 1 ? ` · ${c.department}` : ''}{c.active === false ? ' (inactive)' : ''}</option>)}
              </select>
              {!(form.departments || []).length && <p className="text-[10px] text-[#525252] mt-1">Select a Trade first.</p>}
            </div>

            <div>
              <Label className="text-xs">Brand</Label>
              <select data-testid="inv-item-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3">
                <option value="">Unassigned</option>
                {optsFor(brandsAll, form.brand).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Preferred Supplier</Label>
              <select data-testid="inv-item-form-supplier" value={form.preferred_supplier} onChange={(e) => setForm({ ...form, preferred_supplier: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3">
                <option value="">Unassigned</option>
                {optsFor(suppliersAll, form.preferred_supplier).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div><Label className="text-xs">SKU</Label><Input data-testid="inv-item-form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" /></div>
            <div><Label className="text-xs">Barcode</Label><Input data-testid="inv-item-form-barcode" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" /></div>

            <div><Label className="text-xs">Vendor / Supplier SKU (optional)</Label><Input data-testid="inv-item-form-vendor-sku" value={form.vendor_sku} onChange={(e) => setForm({ ...form, vendor_sku: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" /></div>
            <div><Label className="text-xs">Unit</Label><Input data-testid="inv-item-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="ea, ft, gal…" className="bg-[#0A0A0A] border-[#262626] text-white" /></div>

            {/* Pricing: Cost + Markup% → suggested Sell (markup ON COST, not margin) */}
            <div><Label className="text-xs">Default cost ($)</Label><Input data-testid="inv-item-form-cost" type="number" step="0.01" value={form.default_cost} onChange={(e) => onCost(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div><Label className="text-xs">Markup %</Label><Input data-testid="inv-item-form-markup" type="number" step="1" value={form.markup_pct} onChange={(e) => onMarkup(e.target.value)} placeholder="e.g. 20" className="bg-[#0A0A0A] border-[#262626] text-white" /></div>

            <div className="col-span-2">
              <Label className="text-xs">Sell price ($)</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="inv-item-form-price" type="number" step="0.01" value={form.sell_price} onChange={(e) => onSell(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white flex-1" />
                <Button data-testid="inv-item-use-calculated" type="button" onClick={useCalculated} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 whitespace-nowrap h-10">Use Calculated</Button>
              </div>
              <p data-testid="inv-item-suggested-price" className="text-[11px] text-[#A3A3A3] mt-1">
                Suggested: <span className="text-white">{suggested === '' ? '—' : money(suggested)}</span>
                {form.sell_price_override && <span className="text-amber-400" data-testid="inv-item-price-overridden"> · manually overridden</span>}
              </p>
            </div>

            <div><Label className="text-xs">Company minimum stock</Label><Input data-testid="inv-item-form-min" type="number" step="1" value={form.min_stock_company} onChange={(e) => setForm({ ...form, min_stock_company: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div className="col-span-2"><Label className="text-xs">Description</Label><Textarea data-testid="inv-item-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          </div>
          <div className="flex justify-between items-center gap-2 pt-2 border-t border-[#262626] mt-1">
            <div className="flex gap-2">
              {editing && (editing.archived
                ? <Button data-testid="inv-item-modal-unarchive" onClick={archiveFromModal} variant="outline" className="border-green-700/40 text-green-400 hover:bg-green-700/10"><ArchiveRestore className="w-4 h-4 mr-1" /> Reactivate</Button>
                : <Button data-testid="inv-item-modal-archive" onClick={archiveFromModal} variant="outline" className="border-amber-700/40 text-amber-400 hover:bg-amber-900/20"><Archive className="w-4 h-4 mr-1" /> Archive Item</Button>)}
              {editing && <Button data-testid="inv-item-modal-delete" onClick={deleteFromModal} variant="ghost" className="text-red-400 hover:bg-red-900/20 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete permanently</Button>}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setOpen(false)} variant="outline" className="border-[#262626]">Cancel</Button>
              <Button data-testid="inv-item-save" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{editing ? 'Save' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ManageListsDialog open={manageOpen} onOpenChange={setManageOpen} brands={brandsAll} suppliers={suppliersAll} departments={departmentsAll} categories={categoriesAll} reload={loadLookups} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { refresh(); loadLookups(); }} />
      <BulkPricingDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        selectedIds={[...selected]}
        currentSearch={q}
        departments={departmentsAll}
        categories={categoriesAll}
        brands={brandsAll}
        suppliers={suppliersAll}
        onDone={() => { refresh(); loadLookups(); }}
      />
      <ItemDetailDialog itemId={viewId} open={viewOpen} onOpenChange={setViewOpen} onEdit={(it) => { setViewOpen(false); openEdit(it); }} onChanged={refresh} />
    </div>
  );
};

// ── Brands & Suppliers management (add / rename / deactivate / reactivate) ──
const ManageListsDialog = ({ open, onOpenChange, brands, suppliers, departments, categories, reload }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-manage-lists-dialog" className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Departments, Categories, Brands & Suppliers</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <LookupManager title="Departments" endpoint="departments" rows={departments} reload={reload} testKey="department" />
            <Link to="/admin/departments" data-testid="inv-manage-all-departments-link" className="text-[11px] text-[#D4AF37] hover:underline">Manage all departments (types, order, usage) →</Link>
          </div>
          <LookupManager title="Brands" endpoint="brands" rows={brands} reload={reload} testKey="brand" />
          <LookupManager title="Suppliers" endpoint="suppliers" rows={suppliers} reload={reload} testKey="supplier" />
        </div>
        <div className="border-t border-[#262626] mt-5 pt-5">
          <CategoryManager categories={categories} departments={departments} reload={reload} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Department-scoped Category management (add/rename/change-dept/reorder/(de)activate) ──
const CategoryManager = ({ categories, departments, reload }) => {
  const activeDepts = departments.filter((d) => d.active !== false).map((d) => d.name);
  const [dept, setDept] = useState('');
  useEffect(() => { if (!dept && activeDepts.length) setDept(activeDepts[0]); }, [activeDepts, dept]);
  const [adding, setAdding] = useState('');
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');

  const rows = categories.filter((c) => c.department === dept).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const add = async () => {
    const name = adding.trim();
    if (!name || !dept) return;
    try { await inv.post('/categories', { name, department: dept }); setAdding(''); toast.success('Category added'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveRename = async (r) => {
    const name = editVal.trim();
    if (!name || name === r.name) { setEditId(null); return; }
    try { await inv.put(`/categories/${r.id}`, { name }); setEditId(null); toast.success('Renamed — items updated'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const changeDept = async (r, newDept) => {
    try { await inv.put(`/categories/${r.id}`, { department: newDept }); toast.success('Department changed'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (r) => {
    try { await inv.put(`/categories/${r.id}`, { active: r.active === false }); toast.success(r.active === false ? 'Reactivated' : 'Deactivated'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const move = async (idx, dir) => {
    const arr = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await inv.post('/categories/reorder', { ordered_ids: arr.map((c) => c.id) }); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="inv-lookup-category">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold">Categories in</p>
        <select data-testid="inv-category-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)} className="h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
          {activeDepts.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Input data-testid="inv-lookup-category-new" value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={`Add category to ${dept || '…'}`} className="bg-[#0A0A0A] border-[#262626] text-white h-9 text-sm" />
        <Button data-testid="inv-lookup-category-add" onClick={add} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {rows.length === 0 && <p className="text-[#525252] text-xs">No categories in {dept || 'this department'} yet.</p>}
        {rows.map((r, idx) => (
          <div key={r.id} data-testid={`inv-lookup-category-row-${r.id}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#525252] hover:text-white disabled:opacity-30 leading-none text-xs">▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="text-[#525252] hover:text-white disabled:opacity-30 leading-none text-xs">▼</button>
            </div>
            {editId === r.id ? (
              <>
                <Input data-testid="inv-lookup-category-rename-input" value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveRename(r); }} className="bg-[#141414] border-[#262626] text-white h-7 text-sm flex-1" />
                <Button data-testid="inv-lookup-category-rename-save" onClick={() => saveRename(r)} size="sm" variant="ghost" className="h-7 px-2 text-green-400"><Check className="w-4 h-4" /></Button>
                <Button onClick={() => setEditId(null)} size="sm" variant="ghost" className="h-7 px-2 text-[#A3A3A3]"><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <span className={`flex-1 text-sm ${r.active === false ? 'text-[#525252] line-through' : 'text-white'}`}>{r.name}{r.active === false && <span className="ml-2 text-[10px] uppercase text-[#525252]">inactive</span>}</span>
                <select data-testid={`inv-lookup-category-dept-${r.id}`} value={r.department} onChange={(e) => changeDept(r, e.target.value)} className="h-7 rounded bg-[#141414] border border-[#262626] text-[#A3A3A3] text-xs px-1">
                  {activeDepts.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button data-testid={`inv-lookup-category-rename-${r.id}`} onClick={() => { setEditId(r.id); setEditVal(r.name); }} size="sm" variant="ghost" className="h-7 px-2 text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button data-testid={`inv-lookup-category-toggle-${r.id}`} onClick={() => toggle(r)} size="sm" variant="ghost" className={`h-7 px-2 ${r.active === false ? 'text-green-400' : 'text-amber-400'}`}><Power className="w-3.5 h-3.5" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const LookupManager = ({ title, endpoint, rows, reload, testKey }) => {
  const [adding, setAdding] = useState('');
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');

  const add = async () => {
    const name = adding.trim();
    if (!name) return;
    try { await inv.post(`/${endpoint}`, { name }); setAdding(''); toast.success(`${title.slice(0, -1)} added`); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveRename = async (r) => {
    const name = editVal.trim();
    if (!name || name === r.name) { setEditId(null); return; }
    try { await inv.put(`/${endpoint}/${r.id}`, { name }); setEditId(null); toast.success('Renamed — existing items updated'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (r) => {
    try { await inv.put(`/${endpoint}/${r.id}`, { active: r.active === false }); toast.success(r.active === false ? 'Reactivated' : 'Deactivated'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid={`inv-lookup-${testKey}`}>
      <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2">{title}</p>
      <div className="flex items-center gap-2 mb-3">
        <Input data-testid={`inv-lookup-${testKey}-new`} value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={`Add ${title.slice(0, -1).toLowerCase()}…`} className="bg-[#0A0A0A] border-[#262626] text-white h-9 text-sm" />
        <Button data-testid={`inv-lookup-${testKey}-add`} onClick={add} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {rows.length === 0 && <p className="text-[#525252] text-xs">None yet.</p>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`inv-lookup-${testKey}-row-${r.id}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
            {editId === r.id ? (
              <>
                <Input data-testid={`inv-lookup-${testKey}-rename-input`} value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveRename(r); }} className="bg-[#141414] border-[#262626] text-white h-7 text-sm flex-1" />
                <Button data-testid={`inv-lookup-${testKey}-rename-save`} onClick={() => saveRename(r)} size="sm" variant="ghost" className="h-7 px-2 text-green-400"><Check className="w-4 h-4" /></Button>
                <Button onClick={() => setEditId(null)} size="sm" variant="ghost" className="h-7 px-2 text-[#A3A3A3]"><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <span className={`flex-1 text-sm ${r.active === false ? 'text-[#525252] line-through' : 'text-white'}`}>{r.name}{r.active === false && <span className="ml-2 text-[10px] uppercase text-[#525252]">inactive</span>}</span>
                <Button data-testid={`inv-lookup-${testKey}-rename-${r.id}`} onClick={() => { setEditId(r.id); setEditVal(r.name); }} size="sm" variant="ghost" className="h-7 px-2 text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button data-testid={`inv-lookup-${testKey}-toggle-${r.id}`} onClick={() => toggle(r)} size="sm" variant="ghost" className={`h-7 px-2 ${r.active === false ? 'text-green-400' : 'text-amber-400'}`}><Power className="w-3.5 h-3.5" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Expanded Item View (read-only) with primary product photo ──────────────
const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</p>
    <p className="text-sm text-white break-words">{value === null || value === undefined || value === '' ? '—' : value}</p>
  </div>
);

const ItemDetailDialog = ({ itemId, open, onOpenChange, onEdit, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [photoV, setPhotoV] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try { const { data } = await inv.get(`/items/${itemId}`); setDetail(data); }
    catch { toast.error('Could not load item'); }
    setLoading(false);
  }, [itemId]);

  useEffect(() => { if (open && itemId) { setDetail(null); load(); } }, [open, itemId, load]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { default: axios } = await import('axios');
      await axios.post(`${INV_API}/items/${itemId}/photo?kind=item`, fd, { withCredentials: true });
      toast.success('Photo saved'); setPhotoV((v) => v + 1); await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };
  const removePhoto = async () => {
    if (!window.confirm('Remove this product photo?')) return;
    try {
      const { default: axios } = await import('axios');
      await axios.delete(`${INV_API}/items/${itemId}/photo?kind=item`, { withCredentials: true });
      toast.success('Photo removed'); setPhotoV((v) => v + 1); await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  const d = detail;
  const hasPhoto = !!(d && d.photo_file_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-item-detail-dialog" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {d ? d.name : 'Item'}
            {d && (d.archived ? <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px]">ARCHIVED</Badge> : <Badge className="bg-green-900/30 text-green-400 border-green-700 text-[10px]">ACTIVE</Badge>)}
          </DialogTitle>
        </DialogHeader>
        {!d ? <p className="text-[#A3A3A3] py-8 text-center">{loading ? 'Loading…' : 'No item'}</p> : (
          <div className="space-y-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="sm:w-48 shrink-0">
                <div data-testid="inv-detail-photo" className="aspect-square rounded-md border border-[#262626] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                  {hasPhoto
                    ? <img data-testid="inv-detail-photo-img" src={`${INV_API}/items/${itemId}/photo?kind=item&v=${photoV}`} alt={d.name} className="w-full h-full object-contain" />
                    : <div className="text-[#525252] flex flex-col items-center gap-2 text-xs" data-testid="inv-detail-no-photo"><ImageIcon className="w-8 h-8" /> No photo</div>}
                </div>
                <div className="flex gap-2 mt-2">
                  <label className="flex-1 cursor-pointer">
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="inv-detail-photo-upload" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                    <Button asChild size="sm" variant="outline" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 pointer-events-none"><span><Camera className="w-4 h-4 mr-1" />{hasPhoto ? 'Replace' : 'Upload'}</span></Button>
                  </label>
                  {hasPhoto && <Button data-testid="inv-detail-photo-remove" onClick={removePhoto} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-9 px-2.5"><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <Field label="Trades" value={(d.departments || []).join(', ') || 'No Trade'} />
                <Field label="Size" value={d.size} />
                <Field label="Category" value={d.category} />
                <Field label="Brand" value={d.brand || 'Unassigned'} />
                <Field label="Preferred Supplier" value={d.preferred_supplier || 'Unassigned'} />
                <Field label="SKU" value={d.sku} />
                <Field label="Barcode" value={d.barcode} />
                <Field label="Vendor SKU" value={d.vendor_sku} />
                <Field label="Unit" value={d.unit} />
                <Field label="Default Cost" value={money(d.default_cost)} />
                <Field label="Markup %" value={d.markup_pct !== null && d.markup_pct !== undefined && d.markup_pct !== '' ? `${d.markup_pct}%` : '—'} />
                <Field label="Sell Price" value={d.sell_price !== null && d.sell_price !== undefined ? money(d.sell_price) : '—'} />
                <Field label="Company Min Stock" value={d.min_stock_company || 0} />
              </div>
            </div>
            {d.description && <div><p className="text-[10px] uppercase tracking-wider text-[#525252]">Description</p><p className="text-sm text-white">{d.description}</p></div>}
            <div>
              <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /> Stock by location</p>
              <p className="text-xs mb-2 flex flex-wrap gap-x-4" data-testid="inv-detail-stock-summary">
                <span className="text-white">Shop + Warehouse On Hand (exact): <b data-testid="inv-detail-controlled">{d.controlled_on_hand ?? 0}</b></span>
                <span className="text-[#A3A3A3]">Estimated On Van (rough reference): <b data-testid="inv-detail-van-est">~{d.van_estimated ?? 0}</b></span>
              </p>
              {(!d.stock || d.stock.length === 0) ? <p className="text-[#525252] text-xs" data-testid="inv-detail-no-stock">No stock recorded yet.</p> : (
                <div className="border border-[#262626] rounded overflow-hidden">
                  {d.stock.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm border-b border-[#262626] last:border-0">
                      <span className="text-white">{s.location_name}<span className="text-[#525252] text-xs ml-2">{s.location_type}</span>
                        {s.stock_basis === 'estimated' && <span data-testid={`inv-detail-est-${s.location_id}`} className="ml-2 text-[10px] uppercase tracking-wider text-amber-300/80">Estimated</span>}
                        {s.stock_basis === 'exact' && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400/80">Exact</span>}</span>
                      <span className="text-[#A3A3A3]">{s.stock_basis === 'estimated' ? '~' : ''}{s.quantity}{s.min_stock ? ` (min ${s.min_stock})` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626]">Close</Button>
              <Button data-testid="inv-detail-edit" onClick={() => onEdit(d)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Pencil className="w-4 h-4 mr-2" /> Edit</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const UPDATE_FIELD_OPTS = [
  ['name', 'Name'], ['size', 'Size'], ['default_cost', 'Cost'], ['markup_pct', 'Markup %'],
  ['sell_price', 'Sell Price'], ['category', 'Category'], ['department', 'Department'],
  ['brand', 'Brand'], ['preferred_supplier', 'Supplier'], ['vendor_sku', 'Vendor SKU'],
  ['min_stock_company', 'Company Min'], ['unit', 'Unit'], ['notes', 'Notes'],
];
const ACTION_LABEL = { create: 'Create New', match: 'Match Existing', skip: 'Skip', conflict: 'Conflict', duplicate_in_file: 'Duplicate', error: 'Error' };

// ── Master Inventory Import: upload → preview → resolve managed values → confirm ──
const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [decisions, setDecisions] = useState({}); // `${kind}::${value}` -> {resolution,target,department}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setRows([]); setDecisions({}); setResult(null); };
  useEffect(() => { if (!open) reset(); }, [open]);

  const doPreview = async (f) => {
    if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { default: axios } = await import('axios');
      const { data } = await axios.post(`${INV_API}/items/import/preview`, fd, { withCredentials: true });
      setPreview(data);
      setRows(data.rows.map((r) => ({ ...r, update_fields: [] })));
      // Seed default decisions = leave Unassigned. Compute dept per category value from rows.
      const dec = {};
      ['department', 'brand', 'preferred_supplier', 'category'].forEach((kind) => {
        (data.unmatched_managed[kind] || []).forEach((u) => {
          const key = `${kind}::${u.value}`;
          let dept = '';
          if (kind === 'category') { const rr = data.rows.find((x) => x.category === u.value); dept = (rr && (rr.department_matched || rr.department)) || ''; }
          dec[key] = { resolution: 'unassigned', target: '', department: dept };
        });
      });
      setDecisions(dec);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not parse file'); }
    setBusy(false);
  };

  const onFile = (f) => { setFile(f); doPreview(f); };
  const setRowAction = (i, action) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, action } : r));
  const toggleUpd = (i, field) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, update_fields: r.update_fields.includes(field) ? r.update_fields.filter((f) => f !== field) : [...r.update_fields, field] } : r));
  const setDec = (key, patch) => setDecisions((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const commit = async () => {
    setBusy(true);
    try {
      const commitRows = rows.map((r) => ({
        row: r.row, name: r.name, barcode: r.barcode, size: r.size, department: r.department,
        category: r.category, brand: r.brand, preferred_supplier: r.preferred_supplier,
        vendor_sku: r.vendor_sku, sku: r.sku, unit: r.unit, default_cost: r.default_cost,
        markup_pct: r.markup_pct, sell_price: r.sell_price, min_stock_company: r.min_stock_company,
        notes: r.notes, action: r.action, match_item_id: r.match_item_id, update_fields: r.update_fields || [],
      }));
      const managed_decisions = Object.entries(decisions).map(([key, v]) => {
        const [kind, value] = key.split('::');
        return { kind, value, resolution: v.resolution, target: v.target || null, department: v.department || null };
      });
      const { data } = await inv.post('/items/import/commit', { rows: commitRows, managed_decisions });
      setResult(data);
      toast.success(`Imported — ${data.created} created, ${data.updated} updated`);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setBusy(false);
  };

  const hasUnmatched = preview && Object.values(preview.unmatched_managed || {}).some((a) => a.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-import-dialog" className="bg-[#141414] border-[#262626] text-white max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import Master Inventory</DialogTitle><DialogDescription className="text-[#A3A3A3] text-xs">Upload a CSV/XLSX, review the preview and resolve any unmatched values, then confirm. Nothing is saved until you confirm.</DialogDescription></DialogHeader>

        {!preview && !result && (
          <div className="space-y-4">
            <p className="text-[#A3A3A3] text-sm">Upload a CSV or XLSX. Columns: Item Name, Barcode, Size, Department, Category, Brand, Preferred Supplier, Vendor SKU, SKU, Unit, Default Cost, Markup %, Sell Price, Company Minimum, Notes. Only <b>Item Name + Barcode</b> are required. Nothing is saved until you confirm.</p>
            <label className="block cursor-pointer">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="inv-import-file" onChange={(e) => onFile(e.target.files?.[0])} />
              <div className="border-2 border-dashed border-[#262626] rounded-lg p-10 text-center hover:border-[#D4AF37]/50 transition-colors">
                <Upload className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-white text-sm">{busy ? 'Parsing…' : 'Click to choose a file'}</p>
              </div>
            </label>
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(preview.summary).map(([k, v]) => <Badge key={k} className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3]">{k.replace(/_/g, ' ')}: <span className="text-white ml-1">{v}</span></Badge>)}
            </div>

            {hasUnmatched && (
              <div data-testid="inv-import-unmatched" className="border border-amber-800/40 rounded-md p-3 bg-amber-900/10">
                <p className="text-amber-400 text-xs font-bold uppercase tracking-wide mb-2">Unmatched Managed Values — choose how to handle each</p>
                <div className="space-y-2">
                  {['department', 'brand', 'preferred_supplier', 'category'].flatMap((kind) => (preview.unmatched_managed[kind] || []).map((u) => {
                    const key = `${kind}::${u.value}`; const dec = decisions[key] || {};
                    const existing = kind === 'department' ? preview.managed.departments : kind === 'brand' ? preview.managed.brands : kind === 'preferred_supplier' ? preview.managed.suppliers : preview.managed.categories.map((c) => c.name);
                    return (
                      <div key={key} data-testid={`inv-import-unmatched-${key}`} className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-[#A3A3A3] w-28 capitalize">{kind.replace('preferred_supplier', 'supplier').replace('_', ' ')}</span>
                        <span className="text-white font-medium">"{u.value}"</span>
                        <span className="text-[#525252] text-xs">×{u.count}</span>
                        <select data-testid={`inv-import-decision-${key}`} value={dec.resolution} onChange={(e) => setDec(key, { resolution: e.target.value })} className="h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-2">
                          <option value="unassigned">Leave Unassigned</option>
                          <option value="create">Create new "{u.value}"</option>
                          <option value="map">Map to existing…</option>
                        </select>
                        {dec.resolution === 'map' && (
                          <select data-testid={`inv-import-map-${key}`} value={dec.target || ''} onChange={(e) => setDec(key, { target: e.target.value })} className="h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-2">
                            <option value="">Select…</option>
                            {[...new Set(existing)].map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                        )}
                      </div>
                    );
                  }))}
                </div>
              </div>
            )}

            <div className="border border-[#262626] rounded-md overflow-x-auto max-h-[45vh]">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] border-b border-[#262626] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Item</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Size</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Barcode</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Existing Match</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Action</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Errors / Update</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`inv-import-row-${r.row}`} className="border-b border-[#262626] last:border-0 align-top">
                      <td className="px-2 py-1.5 text-white">{r.name || <span className="text-red-400">—</span>}{r.name_conflict && <Badge className="ml-1 bg-amber-900/30 text-amber-400 border-amber-700 text-[9px]">NAME DIFFERS</Badge>}</td>
                      <td className="px-2 py-1.5 text-[#A3A3A3]">{r.size || '—'}</td>
                      <td className="px-2 py-1.5 text-[#A3A3A3] font-mono">{r.barcode || '—'}</td>
                      <td className="px-2 py-1.5 text-[#A3A3A3]">{r.match_item_name || '—'}</td>
                      <td className="px-2 py-1.5">
                        {['duplicate_in_file', 'error'].includes(r.action) ? (
                          <Badge className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3]">{ACTION_LABEL[r.action]}</Badge>
                        ) : (
                          <select data-testid={`inv-import-action-${r.row}`} value={r.action} onChange={(e) => setRowAction(i, e.target.value)} className="h-7 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-1">
                            {r.match_item_id ? <><option value="match">Match / Update</option><option value="skip">Skip</option></> : <><option value="create">Create New</option><option value="skip">Skip</option></>}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.errors?.length > 0 && <span className="text-red-400">{r.errors.join('; ')}</span>}
                        {(r.action === 'match' || r.action === 'conflict') && r.match_item_id && (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            <span className="text-[#525252]">Overwrite:</span>
                            {UPDATE_FIELD_OPTS.map(([f, lbl]) => (
                              <label key={f} className="flex items-center gap-0.5 cursor-pointer text-[#A3A3A3]">
                                <input type="checkbox" data-testid={`inv-import-upd-${r.row}-${f}`} checked={r.update_fields.includes(f)} onChange={() => toggleUpd(i, f)} className="accent-[#D4AF37]" />{lbl}
                              </label>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between gap-2">
              <Button onClick={reset} variant="outline" className="border-[#262626]">Choose Different File</Button>
              <Button data-testid="inv-import-confirm" onClick={commit} disabled={busy} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{busy ? 'Importing…' : 'Confirm Import'}</Button>
            </div>
          </div>
        )}

        {result && (
          <div data-testid="inv-import-result" className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Stat2 label="Created" value={result.created} />
              <Stat2 label="Updated" value={result.updated} />
              <Stat2 label="Skipped" value={result.skipped} />
              <Stat2 label="Barcodes registered" value={result.barcodes_registered} />
            </div>
            {result.errors > 0 && <p className="text-red-400 text-sm">{result.errors} row(s) errored.</p>}
            <div className="flex justify-end"><Button data-testid="inv-import-done" onClick={() => onOpenChange(false)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat2 = ({ label, value }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2 text-center">
    <p className="text-2xl font-bold text-[#D4AF37]">{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</p>
  </div>
);

export default InventoryItems;
